import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { submissions } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Upload, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Submissions = () => {
  const { language } = useLanguage();
  const t = submissions[language];
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    title: '',
    synopsis: '',
    file: null
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('synopsis', formData.synopsis);
      formDataToSend.append('file', formData.file);

      const response = await axios.post(`${API}/submissions`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: language === 'en' ? 'Submission Received!' : 'تم استلام الطلب!',
        description: language === 'en' 
          ? 'Your script has been successfully submitted.' 
          : 'تم تقديم سيناريوك بنجاح.'
      });

      setFormData({ name: '', email: '', category: '', title: '', synopsis: '', file: null });
      const fileInput = document.getElementById('file');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: language === 'en' ? 'Error' : 'خطأ',
        description: error.response?.data?.detail || 
          (language === 'en' ? 'Failed to submit. Please try again.' : 'فشل التقديم. يرجى المحاولة مرة أخرى.'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page py-32">
      <div className="container mx-auto px-8">
        <div className="max-w-4xl mx-auto text-center mb-20 animate-fade-in">
          <h1 className="heading-1 text-gold mb-6">{t.title}</h1>
          <p className="body-large text-white/90">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto mb-20">
          {t.categories.map((category, index) => (
            <Card
              key={category.id}
              className="bg-card-dark border-gold/20 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-105 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6 text-center">
                <h3 className="heading-6 text-gold mb-2">{category.name}</h3>
                <p className="caption text-gray-400">{category.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="heading-4 text-gold text-center mb-8 animate-fade-in">
            {language === 'en' ? 'Important Dates' : 'المواعيد المهمة'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.deadlines.map((deadline, index) => (
              <Card
                key={index}
                className="bg-card-dark border-gold/20 text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1 + 0.4}s` }}
              >
                <CardContent className="p-6">
                  <p className="caption text-gold mb-2">{deadline.type}</p>
                  <p className="body-medium text-white mb-2">{deadline.date}</p>
                  <p className="heading-5 text-gold">{deadline.fee}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Card className="max-w-3xl mx-auto bg-card-dark border-gold/20 animate-fade-in" style={{ animationDelay: '0.7s' }}>
          <CardContent className="p-8 md:p-12">
            <h2 className="heading-4 text-gold mb-8 text-center">{t.ctaButton}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">
                  {language === 'en' ? 'Full Name' : 'الاسم الكامل'}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  {language === 'en' ? 'Email Address' : 'البريد الإلكتروني'}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="category" className="text-white mb-2 block">
                  {language === 'en' ? 'Category' : 'الفئة'}
                </Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))} required disabled={loading}>
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue placeholder={language === 'en' ? 'Select category' : 'اختر الفئة'} />
                  </SelectTrigger>
                  <SelectContent>
                    {t.categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title" className="text-white mb-2 block">
                  {language === 'en' ? 'Script Title' : 'عنوان السيناريو'}
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="synopsis" className="text-white mb-2 block">
                  {language === 'en' ? 'Brief Synopsis (500 words max)' : 'ملخص موجز (500 كلمة كحد أقصى)'}
                </Label>
                <textarea
                  id="synopsis"
                  name="synopsis"
                  value={formData.synopsis}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  rows={5}
                  className="w-full rounded-md bg-white/5 border border-white/20 text-white p-3 resize-none focus:border-gold focus:ring-1 focus:ring-gold"
                />
              </div>

              <div>
                <Label htmlFor="file" className="text-white mb-2 block">
                  {language === 'en' ? 'Upload Script (PDF)' : 'رفع السيناريو (PDF)'}
                </Label>
                <Input
                  id="file"
                  name="file"
                  type="file"
                  accept=".pdf"
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-gold file:text-black file:cursor-pointer hover:file:bg-gold/90"
                />
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {language === 'en' ? 'Submitting...' : 'جاري التقديم...'}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-5 w-5" />
                    {t.ctaButton}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Submissions;
